import { mergeDeepRight } from 'ramda';
import React, { useMemo } from 'react';

import EnvironmentContext, {
  EnvironmentData,
  Environment as EnvironmentEnum,
} from '../contexts/EnvironmentContext';

// In order of precedence

/*
Dec 2024, these are the environments we have:
Production via fastly
Production origin

Staging non-iac via fastly -- we want to get rid of this and move to iac.
Staging non-iac origin -- Cluster = non-prod-b2b Non IaC

Staging shared IaC via fastly -- will be done once the origin is working
Staging shared IaC origin -- Cluster = non-prod-shared (IaC)

Dev environment origin (no fastly) -- non-prod-shared (IaC)
Local
*/

const localHostnameRegex = /.+\.local$/;

// Staging non-iac fastly and origin
// Client fastly URL is www.stg.timeshighereducation.com/datapoints
// Client cluster URL is datapoints.stg.the-internal.com/datapoints
const stagingNonIACGenericRegex = /.+\.stg\..+$/;

// Staging shared IaC will eventually be staging
const stagingSharedIaCHostnameRegex = /datapoints\.staging-shared\.the-internal\.cloud$/;

// Prod shared IaC will eventually be prod
const prodSharedIaCHostnameRegex = /datapoints\.prod-shared\.the-internal\.cloud$/;

// Prod load testing environment
const prodLoadTestHostnameRegex = /datapoints-lt\.prod-shared\.the-internal\.cloud$/;

// Staging iac fastly is not here yet, but would be same as above (www.stg.timeshighereducation.com/datapoints)
// Client cluster iac URL is datapoints.staging.the-internal.cloud/datapoints
// Beware this will match staging-shared as well
const stagingIACGenericRegex = /.+\.staging\..+$/;

// Client Prod via fastly is www.timeshighereducation.com/datapoints
// Client Prod origin is datapoints.prd.the-internal.com/datapoints. It won't match the below regex, so it probably doesn't work
const prodHostnameRegex = /^www\.timeshighereducation\.com$/;

// We are deprecating this dev environment
// Client Dev via fastly is not going to be done
// Client Dev origin is datapoints-dev.stg.the-internal.com
// const devNonIacHostnameRegex = /.+-dev\.stg\.the-internal\.com$/;

// Client Dev via fastly is not going to be done
// Client Dev origin is datapoints-dev.staging-shared.the-internal.cloud
const devStagingSharedIaCHostnameRegex = /.+-dev\.staging-shared\.the-internal\.cloud$/;

const FIRST_SUBDOMAIN_REGEX = /^([\w-_]+)/;

// Because we are using react, we cannot dynamically set the server_api address, so we use this method instead:
const calculateApiEndpoint = (hostname: string, port: number) => {
  /*
  As of Dev 2024, the server_api for datapoints ingresses are:
  prod via fastly = api.datapoints.timeshighereducation.com/datapoints
  prod origin = datapoints-api.prd.the-internal.com/datapoints

  staging (Non-Prod B2B) via fastly = api.stg.timeshighereducation.com/datapoints
  staging (Non-Prod B2B) origin = datapoints-api.stg.the-internal.com/datapoints -- It is in the B2B staging non-IAC cluster

  staging (shared IaC) via fastly not done yet (but the URL will be the same as above)
  staging (shared IaC) origin = datapoints.staging-shared.the-internal.cloud/datapoint

  dev (Non-Prod Shared) via fastly is not going to be put in place
  dev (Non-Prod Shared IaC) origin = datapoints-api-dev.staging-shared.the-internal.cloud/datapoints
  */

  if (devStagingSharedIaCHostnameRegex.test(hostname)) {
    // Dev doesn't use the normal pattern
    return 'datapoints-api-dev.staging-shared.the-internal.cloud/datapoints';
  }
  if (stagingSharedIaCHostnameRegex.test(hostname)) {
    return 'datapoints-api.staging-shared.the-internal.cloud/datapoints';
  }
  if (prodLoadTestHostnameRegex.test(hostname)) {
    return 'datapoints-api-lt.prod-shared.the-internal.cloud/datapoints';
  }
  if (prodSharedIaCHostnameRegex.test(hostname)) {
    return 'datapoints-api.prod-shared.the-internal.cloud/datapoints';
  }

  const apiHostname = hostname.includes('.')
    ? hostname.replace(FIRST_SUBDOMAIN_REGEX, 'api')
    : `api.${hostname}`;

  const serverApiUrl =
    port !== 80
      ? `${apiHostname}:${port}/datapoints`
      : `${apiHostname}/datapoints`;

  // eslint-disable-next-line no-console
  // console.log('hostname: ', hostname, ' calculateApiEndpoint:', serverApiUrl);

  return serverApiUrl;
};

const commonConfig = {
  keycloakConfig: {
    realm: 'THE',
    clientId: 'DataPoints',
  },
  newRelicConfig: {
    accountId: '2378272',
    licenseKey: 'da4493c478',
  },
};

const getBuildMetaValue = (htmlEl: HTMLHtmlElement, metaName: string) => {
  const metaElement = htmlEl.querySelector(`[name~=${metaName}]`);
  return metaElement?.getAttribute('content') || '';
};

export const getBuildInfo = (
  htmlEl: HTMLHtmlElement,
): { version: string; date: string } => ({
  version: getBuildMetaValue(htmlEl, 'build-version'),
  date: getBuildMetaValue(htmlEl, 'build-date'),
});

const Environment: React.FC<{}> = ({ children }) => {
  const { hostname, port, origin } = window.location;

  const apiEndpoint = useMemo(
    () => calculateApiEndpoint(hostname, parseInt(port, 10) || 80),
    [hostname, port],
  );

  const baseUrl = `${origin}/datapoints/`;

  const environmentData: EnvironmentData = useMemo(() => {
    if (localHostnameRegex.test(hostname)) {
      return mergeDeepRight(commonConfig, {
        enableTracking: false,
        keycloakConfig: {
          // should be https://auth.timeshighereducation.local/auth/
          url: 'https://auth.stg.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128aff0ebb94525cd03dc33',
        },
        newRelicConfig: {
          appId: null,
        },
        classicUrl: 'https://classic.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.DEV,
        // graURL:
        //   'https://generic-ranking-api.staging-shared.the-internal.cloud/graphql',
        graURL:
          'https://generic-ranking-api.timeshighereducation.local/graphql',
        reputationUrl:
          'https://new-reputation.timeshighereducation.local/datapoints/',
        theInsightsUrl: 'https://www.stg.timeshighereducation.com/insights',
      });
    }
    if (devStagingSharedIaCHostnameRegex.test(hostname)) {
      // Dev should use the staging GRA and keycloak
      return mergeDeepRight(commonConfig, {
        enableTracking: false,
        keycloakConfig: {
          url: 'https://auth.stg.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128aff0ebb94525cd03dc33',
        },
        newRelicConfig: {
          appId: null,
        },
        classicUrl: 'https://classic.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.DEV,
        graURL:
          'https://generic-ranking-api.staging-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.stg.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.stg.timeshighereducation.com/insights',
      });
    }
    if (stagingSharedIaCHostnameRegex.test(hostname)) {
      // Dev should use the staging GRA and keycloak
      return mergeDeepRight(commonConfig, {
        enableTracking: false,
        keycloakConfig: {
          url: 'https://auth.stg.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128aff0ebb94525cd03dc33',
        },
        newRelicConfig: {
          appId: null,
        },
        classicUrl: 'https://classic.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.DEV,
        graURL:
          'https://generic-ranking-api.staging-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.stg.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.stg.timeshighereducation.com/insights',
      });
    }
    if (prodLoadTestHostnameRegex.test(hostname)) {
      return mergeDeepRight(commonConfig, {
        enableTracking: true,
        keycloakConfig: {
          url: 'https://auth.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128af9a1e1e97285771b682',
        },
        newRelicConfig: {
          appId: '288159060',
        },
        classicUrl: 'https://classic.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.PRODUCTION,
        graURL:
          'https://generic-ranking-api-lt.prod-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.timeshighereducation.com/insights',
      });
    }
    if (prodSharedIaCHostnameRegex.test(hostname)) {
      return mergeDeepRight(commonConfig, {
        enableTracking: true,
        keycloakConfig: {
          url: 'https://auth.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128af9a1e1e97285771b682',
        },
        newRelicConfig: {
          appId: '288159060',
        },
        classicUrl: 'https://classic.timeshighereducation.com/datapoints',
        environment: prodHostnameRegex.test(hostname)
          ? EnvironmentEnum.PRODUCTION
          : EnvironmentEnum.UNKNOWN,
        graURL:
          'https://generic-ranking-api.prod-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.timeshighereducation.com/insights',
      });
    }
    if (stagingNonIACGenericRegex.test(hostname)) {
      return mergeDeepRight(commonConfig, {
        enableTracking: true,
        keycloakConfig: {
          url: 'https://auth.stg.timeshighereducation.com/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128afd889a2d10d79db617c',
        },
        newRelicConfig: {
          appId: '288153577',
        },
        classicUrl: 'https://classic.stg.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.STAGING,
        graURL:
          'https://generic-ranking-api.staging-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.stg.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.stg.timeshighereducation.com/insights',
      });
    }
    if (stagingIACGenericRegex.test(hostname)) {
      return mergeDeepRight(commonConfig, {
        enableTracking: true,
        keycloakConfig: {
          url: 'https://auth.staging.the-internal.cloud/auth/',
        },
        launchDarklyConfig: {
          clientSideID: '6128afd889a2d10d79db617c',
        },
        newRelicConfig: {
          appId: '288153577',
        },
        classicUrl: 'https://classic.stg.timeshighereducation.com/datapoints',
        environment: EnvironmentEnum.STAGING,
        graURL:
          'https://generic-ranking-api.staging-shared.the-internal.cloud/graphql',
        reputationUrl: 'https://www.stg.timeshighereducation.com/datapoints/',
        theInsightsUrl: 'https://www.stg.timeshighereducation.com/insights',
      });
    }
    // Default is Prod
    return mergeDeepRight(commonConfig, {
      enableTracking: true,
      keycloakConfig: {
        url: 'https://auth.timeshighereducation.com/auth/',
      },
      launchDarklyConfig: {
        clientSideID: '6128af9a1e1e97285771b682',
      },
      newRelicConfig: {
        appId: '288159060',
      },
      classicUrl: 'https://classic.timeshighereducation.com/datapoints',
      environment: prodHostnameRegex.test(hostname)
        ? EnvironmentEnum.PRODUCTION
        : EnvironmentEnum.UNKNOWN,
      graURL: 'https://gra.timeshighereducation.com/graphql',
      reputationUrl: 'https://www.timeshighereducation.com/datapoints/',
      theInsightsUrl: 'https://www.timeshighereducation.com/insights',
    });
  }, [hostname]);

  const contextValue = useMemo(
    () => ({
      ...environmentData,
      apiEndpoint,
      baseUrl,
      ...getBuildInfo(window.document.getElementsByTagName('html')[0]),
    }),
    [environmentData, apiEndpoint, baseUrl],
  );

  return (
    <EnvironmentContext.Provider value={contextValue}>
      {children}
    </EnvironmentContext.Provider>
  );
};

export default Environment;
